import { useState } from "react";
import { MODEL_MAX_KB, MODEL_SLIM_KB, MODEL_WARN_KB, classLoadSeconds } from "@greg-chick/shared";
import { slimGlbFile } from "../lib/slimGlbCanvas";

/**
 * 🧊 **3D 파일 고르기** — 마법 꾸미기 3D 층·AR 보기 ④ 3D 모델이 같이 쓴다(2026-09-14).
 *
 * ## 무엇을 하나
 * · `.glb` 가 아니면 왜 안 되는지 말한다
 * · 가벼우면(1MB 아래) 바로 올린다
 * · 무거우면 **먼저 묻는다** — [🪶 가볍게 줄여서 올리기](권장) · [그대로 올리기] · [취소]
 *   원본은 그대로 아이 폰에 간다. 국가유산청 원본은 37MB 인데 99% 가 그림이라 줄이면 1MB 안쪽이 된다.
 * · 20MB 가 넘으면 그대로는 못 올린다(줄여서만)
 *
 * 올리고 자리에 적는 일은 부르는 칸이 한다(`onUpload`) — 여기는 **무엇을 올릴지**만 정한다.
 * 🔴 `.glb` 는 브라우저가 `File.type` 을 비워 보낸다 — 여기서 `model/gltf-binary` 를 붙여 넘긴다(엣지가 그걸로 거른다).
 */
export function ModelFilePicker({ label, onUpload }: { label: string; onUpload: (file: File) => Promise<void> }) {
  const [busy, setBusy] = useState<null | "slim" | "up">(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [held, setHeld] = useState<File | null>(null);

  async function send(file: File, done: string | null): Promise<void> {
    setBusy("up");
    setErr(null);
    try {
      await onUpload(new File([file], file.name, { type: "model/gltf-binary" }));
      setHeld(null);
      setNote(done);
    } catch (e) {
      setErr(`못 올렸어요: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  }

  function pick(file: File | undefined) {
    if (!file) return;
    setErr(null);
    setNote(null);
    setHeld(null);
    if (!/\.glb$/i.test(file.name)) {
      setErr("`.glb` 파일만 받아요(glTF 바이너리). Sketchfab 에서 「glTF」로 받으면 안에 .glb 가 있어요.");
      return;
    }
    if (file.size / 1024 > MODEL_SLIM_KB) {
      setHeld(file); // 무겁다 — 줄일지 먼저 묻는다
      return;
    }
    void send(file, null);
  }

  async function slim(file: File) {
    setBusy("slim");
    setErr(null);
    let result: Awaited<ReturnType<typeof slimGlbFile>>;
    try {
      result = await slimGlbFile(file);
    } catch (e) {
      setBusy(null);
      setErr(`줄이지 못했어요: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    const { file: light, report } = result;
    if (report.afterKb > MODEL_MAX_KB) {
      setBusy(null);
      setErr(`줄여도 ${sizeText(report.afterKb)} 라 너무 커요 — 그림(텍스처)이 더 작은 판을 올려 주세요.`);
      return;
    }
    await send(
      light,
      `🪶 ${sizeText(report.beforeKb)} → ${sizeText(report.afterKb)} 로 줄여 올렸어요 — 교실 회선 ${classLoadSeconds(report.afterKb).toFixed(1)}초. ` +
        `그림 ${report.textures.length}장을 다시 굽고${report.droppedTangents ? " 안 쓰는 접선 정보를 뺐어요" : "었어요"}. 모양은 그대로예요.`,
    );
  }

  const h = held;
  const heldKb = h ? h.size / 1024 : 0;
  const tooBig = heldKb > MODEL_MAX_KB;
  return (
    <div className="model-pick" data-model-pick>
      <label className="slot__file">
        {busy === "up" ? "올리는 중…" : busy === "slim" ? "줄이는 중…" : ".glb 고르기"}
        <input
          type="file"
          accept=".glb,model/gltf-binary"
          hidden
          disabled={!!busy}
          aria-label={`${label} 3D 파일 고르기`}
          onChange={(e) => {
            pick(e.target.files?.[0]);
            e.target.value = ""; // 같은 파일을 다시 골라도 알게
          }}
        />
      </label>
      {h ? (
        <div className="model-pick__ask" data-model-pick-ask>
          <p>
            {heldKb > MODEL_WARN_KB ? "🐘 " : ""}
            <b>{h.name}</b> {sizeText(heldKb)} — 교실 회선에서 <b>{Math.round(classLoadSeconds(heldKb))}초</b>쯤 걸려요. 원본이 그대로 아이 폰에 가요.
          </p>
          <div className="model-pick__btns">
            <button type="button" className="btn btn--primary btn--sm" disabled={!!busy} onClick={() => void slim(h)}>
              🪶 가볍게 줄여서 올리기
            </button>
            <button type="button" className="btn btn--sm" disabled={!!busy || tooBig} onClick={() => void send(h, heldKb > MODEL_WARN_KB ? `🐘 그대로 올렸어요 — 교실 회선에서 ${Math.round(classLoadSeconds(heldKb))}초쯤 걸려요.` : null)}>
              그대로 올리기
            </button>
            <button type="button" className="btn btn--sm" disabled={!!busy} onClick={() => setHeld(null)}>
              취소
            </button>
          </div>
          {tooBig ? <p className="hint">20MB 가 넘어 그대로는 못 올려요 — 줄여서 올려 주세요.</p> : null}
          <p className="hint">줄이기: 그림(텍스처)을 바탕색 1024px·나머지 512px 로 다시 굽고, 안 쓰는 접선 정보를 빼요. 모양은 그대로예요.</p>
        </div>
      ) : null}
      {note ? (
        <p className="hint" data-model-pick-note>
          {note}
        </p>
      ) : null}
      {err ? <p className="hint">⚠️ {err}</p> : null}
    </div>
  );
}

function sizeText(kb: number): string {
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)}MB` : `${Math.round(kb)}KB`;
}
